import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Tooltip,
  Skeleton,
  Alert,
  Pagination,
  SelectChangeEvent,
  Card,
  CardContent,
  Stack,
  useTheme,
  useMediaQuery,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from "@mui/material";
import {
  Search,
  Quiz,
  ExpandMore,
  ExpandLess,
  Visibility,
  CheckCircle,
  Cancel,
  RadioButtonChecked,
  CheckBox as CheckBoxIcon,
  Notes,
  ArrowDropDown,
  Delete,
} from "@mui/icons-material";
import {
  surveySubmissionsAPI,
  coursesAPI,
  SurveySubmission,
  SurveySubmissionAnswer,
} from "../../services/adminApi";

const COLORS = {
  accent: "#1565C0",
  survey: "#7C3AED",
  surveyBg: "#F5F3FF",
  text: "#0A1628",
  border: "rgba(0,0,0,0.08)",
};

const ANSWER_TYPE_ICON: Record<string, React.ReactNode> = {
  radio: <RadioButtonChecked sx={{ fontSize: 14 }} />,
  checkbox: <CheckBoxIcon sx={{ fontSize: 14 }} />,
  text: <Notes sx={{ fontSize: 14 }} />,
  dropdown: <ArrowDropDown sx={{ fontSize: 14 }} />,
};

interface CourseOption {
  _id: string;
  title: string;
}

const AnswerDetailDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  submission: SurveySubmission | null;
}> = ({ open, onClose, submission }) => {
  if (!submission) return null;
  const isQuiz = submission.mode === "quiz";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Quiz sx={{ color: COLORS.survey }} />
          {submission.itemTitle}
        </Box>
      </DialogTitle>

      <Box sx={{ px: 3, pb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {submission.userName} Â· {submission.userEmail}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
          <Chip size="small" label={submission.courseTitle} sx={{ fontSize: "0.7rem", height: 22 }} />
          <Chip size="small" label={submission.phaseTitle} sx={{ fontSize: "0.7rem", height: 22 }} />
          {isQuiz && (
            <Chip
              size="small"
              label={submission.passed ? `Passed Â· ${submission.score}%` : `Failed Â· ${submission.score}%`}
              sx={{
                fontSize: "0.7rem", height: 22, fontWeight: 700,
                bgcolor: submission.passed ? "#ECFDF5" : "#FEF2F2",
                color: submission.passed ? "#10B981" : "#EF4444",
              }}
            />
          )}
        </Box>
      </Box>

      <Divider sx={{ mt: 1.5 }} />

      <DialogContent>
        <Stack spacing={2}>
          {submission.answers.map((ans: SurveySubmissionAnswer, idx: number) => {
            const answerArr = Array.isArray(ans.answer) ? ans.answer : [ans.answer];
            return (
              <Box key={idx}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.5 }}>
                  <Box sx={{ width: 22, height: 22, borderRadius: "6px", bgcolor: COLORS.surveyBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: 0.2, color: COLORS.survey }}>
                    {ANSWER_TYPE_ICON[ans.questionType] ?? <Quiz sx={{ fontSize: 14 }} />}
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    {idx + 1}. {ans.questionText}
                  </Typography>
                </Box>
                <Box sx={{ pl: 4 }}>
                  {answerArr.map((a, ai) => (
                    <Chip
                      key={ai}
                      size="small"
                      label={a || "â€”"}
                      sx={{ mr: 0.5, mb: 0.5, bgcolor: "rgba(0,0,0,0.04)", fontSize: "0.78rem", height: 24 }}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", borderRadius: "8px" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const AdminSurveySubmissions: React.FC = () => {
  const [submissions, setSubmissions] = useState<SurveySubmission[]>([]);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [modeFilter, setModeFilter] = useState<"" | "survey" | "quiz">("");
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSubmission, setDetailSubmission] = useState<SurveySubmission | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Load available courses for the filter dropdown
  useEffect(() => {
    coursesAPI.list({ limit: 100 }).then((res) => {
      setCourses(res.data.courses || []);
    }).catch(() => {});
  }, []);

  const loadSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params: Record<string, any> = { page: pagination.page, limit: pagination.limit };
      if (search) params.search = search;
      if (courseFilter) params.courseId = courseFilter;
      if (modeFilter) params.mode = modeFilter;

      const res = await surveySubmissionsAPI.list(params);
      setSubmissions(res.data.submissions || []);
      setPagination((p) => ({ ...p, ...(res.data.pagination || {}) }));
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, courseFilter, modeFilter]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const handleCourseChange = (e: SelectChangeEvent) => {
    setCourseFilter(e.target.value);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const handleModeChange = (e: SelectChangeEvent) => {
    setModeFilter(e.target.value as "" | "survey" | "quiz");
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  const openDetail = (sub: SurveySubmission) => {
    setDetailSubmission(sub);
    setDetailOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await surveySubmissionsAPI.delete(deleteId);
      setSubmissions((prev) => prev.filter((s) => s._id !== deleteId));
      setPagination((p) => ({ ...p, total: Math.max(0, p.total - 1) }));
      setDeleteId(null);
    } catch {
      setError("Failed to delete submission");
    } finally {
      setDeleting(false);
    }
  };

  const renderSkeleton = () => (
    <Box>{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} variant="rectangular" height={56} sx={{ mb: 1, borderRadius: 1 }} />)}</Box>
  );

  // Mobile card view
  const renderMobileCards = () => (
    <Stack spacing={1.5}>
      {submissions.map((sub) => (
        <Card key={sub._id} elevation={0} sx={{ border: `1px solid ${COLORS.border}`, borderRadius: "12px" }}>
          <CardContent sx={{ pb: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>{sub.itemTitle}</Typography>
              <Chip
                size="small" label={sub.mode}
                sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700, textTransform: "capitalize",
                  bgcolor: sub.mode === "quiz" ? "#EFF6FF" : COLORS.surveyBg,
                  color: sub.mode === "quiz" ? "#2563EB" : COLORS.survey }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {sub.userName} Â· {sub.userEmail}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {sub.courseTitle} / {sub.phaseTitle}
            </Typography>
            {sub.mode === "quiz" && (
              <Box sx={{ display: "flex", gap: 0.8, mt: 0.5 }}>
                <Chip
                  size="small"
                  label={sub.passed ? `Passed (${sub.score}%)` : `Failed (${sub.score}%)`}
                  sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700,
                    bgcolor: sub.passed ? "#ECFDF5" : "#FEF2F2",
                    color: sub.passed ? "#10B981" : "#EF4444" }}
                />
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {formatDate(sub.submittedAt)}
            </Typography>
          </CardContent>
          <Box sx={{ px: 2, pb: 1.5, display: "flex", gap: 1 }}>
            <Button size="small" startIcon={<Visibility />} onClick={() => openDetail(sub)} sx={{ color: COLORS.survey, textTransform: "none" }}>
              View Answers
            </Button>
            <Button size="small" startIcon={<Delete />} onClick={() => setDeleteId(sub._id)} sx={{ color: "#EF4444", textTransform: "none" }}>
              Delete
            </Button>
          </Box>
        </Card>
      ))}
    </Stack>
  );

  // Desktop table
  const renderTable = () => (
    <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${COLORS.border}`, borderRadius: "12px", overflow: "hidden" }}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: "rgba(0,0,0,0.02)" }}>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#6B7280" }}>User</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#6B7280" }}>Survey / Quiz</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#6B7280" }}>Course â€º Phase</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#6B7280" }}>Mode</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#6B7280" }}>Score</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#6B7280" }}>Submitted</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#6B7280" }}>Answers</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {submissions.map((sub) => (
            <React.Fragment key={sub._id}>
              <TableRow hover sx={{ cursor: "pointer", "&:hover": { bgcolor: `${COLORS.surveyBg}80` } }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{sub.userName}</Typography>
                  <Typography variant="caption" color="text.secondary">{sub.userEmail}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                    <Quiz sx={{ fontSize: 16, color: COLORS.survey }} />
                    <Typography variant="body2" fontWeight={500}>{sub.itemTitle}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{sub.courseTitle}</Typography>
                  <Typography variant="caption" color="text.secondary">{sub.phaseTitle}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small" label={sub.mode} sx={{ height: 24, fontSize: "0.72rem", fontWeight: 700, textTransform: "capitalize",
                      bgcolor: sub.mode === "quiz" ? "#EFF6FF" : COLORS.surveyBg,
                      color: sub.mode === "quiz" ? "#2563EB" : COLORS.survey }}
                  />
                </TableCell>
                <TableCell>
                  {sub.mode === "quiz" ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                      {sub.passed
                        ? <CheckCircle sx={{ fontSize: 16, color: "#10B981" }} />
                        : <Cancel sx={{ fontSize: 16, color: "#EF4444" }} />}
                      <Typography variant="body2" fontWeight={700} sx={{ color: sub.passed ? "#10B981" : "#EF4444" }}>
                        {sub.score}%
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">â€”</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.78rem" }}>
                    {formatDate(sub.submittedAt)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                    <Tooltip title="View answers" arrow>
                      <IconButton size="small" onClick={() => openDetail(sub)} sx={{ color: COLORS.survey }}>
                        <Visibility sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={expandedRow === sub._id ? "Collapse" : "Quick preview"} arrow>
                      <IconButton size="small" onClick={() => setExpandedRow(expandedRow === sub._id ? null : sub._id)} sx={{ color: "#9CA3AF" }}>
                        {expandedRow === sub._id ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <IconButton size="small" onClick={() => setDeleteId(sub._id)} sx={{ color: "#EF4444", opacity: 0.7, "&:hover": { opacity: 1 } }}>
                        <Delete sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
              {/* Inline quick preview row */}
              <TableRow>
                <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                  <Collapse in={expandedRow === sub._id} timeout="auto" unmountOnExit>
                    <Box sx={{ px: 3, py: 1.5, bgcolor: `${COLORS.surveyBg}60`, borderBottom: `1px solid ${COLORS.border}` }}>
                      <Stack spacing={0.8}>
                        {sub.answers.map((ans, i) => (
                          <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                            <Typography variant="caption" sx={{ color: "#6B7280", minWidth: 180, fontWeight: 600, pt: 0.3 }}>
                              {i + 1}. {ans.questionText}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                              {(Array.isArray(ans.answer) ? ans.answer : [ans.answer]).map((a, ai) => (
                                <Chip key={ai} size="small" label={a || "â€”"} sx={{ height: 20, fontSize: "0.7rem", bgcolor: "rgba(0,0,0,0.04)" }} />
                              ))}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: COLORS.text }}>
            Survey & Quiz Submissions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and filter all user responses across your courses
          </Typography>
        </Box>
        {pagination.total > 0 && (
          <Chip
            icon={<Quiz />}
            label={`${pagination.total} total submission${pagination.total !== 1 ? "s" : ""}`}
            sx={{ bgcolor: COLORS.surveyBg, color: COLORS.survey, fontWeight: 700, "& .MuiChip-icon": { color: COLORS.survey } }}
          />
        )}
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: "12px", border: `1px solid ${COLORS.border}`, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          size="small" placeholder="Search by user or survey name..."
          value={search} onChange={handleSearchChange}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: "#9CA3AF", fontSize: 20 }} /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Course</InputLabel>
          <Select value={courseFilter} label="Course" onChange={handleCourseChange} sx={{ borderRadius: "10px" }}>
            <MenuItem value="">All Courses</MenuItem>
            {courses.map((c) => (
              <MenuItem key={c._id} value={c._id}>{c.title}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Type</InputLabel>
          <Select value={modeFilter} label="Type" onChange={handleModeChange} sx={{ borderRadius: "10px" }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="survey">Survey</MenuItem>
            <MenuItem value="quiz">Quiz</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{error}</Alert>}

      {/* Content */}
      {loading ? renderSkeleton() : submissions.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: "center", borderRadius: "12px", border: `1px solid ${COLORS.border}` }}>
          <Quiz sx={{ fontSize: 48, color: "#D1D5DB", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>No submissions found</Typography>
          <Typography variant="body2" color="text.secondary">
            {courseFilter || modeFilter || search ? "Try adjusting your filters." : "No users have submitted any surveys or quizzes yet."}
          </Typography>
        </Paper>
      ) : (
        <>
          {isMobile ? renderMobileCards() : renderTable()}
          {pagination.pages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={pagination.pages} page={pagination.page}
                onChange={(_, p) => setPagination((prev) => ({ ...prev, page: p }))}
                sx={{ "& .Mui-selected": { bgcolor: `${COLORS.survey} !important`, color: "#fff" } }}
              />
            </Box>
          )}
        </>
      )}

      {/* Answer Detail Dialog */}
      <AnswerDetailDialog open={detailOpen} onClose={() => setDetailOpen(false)} submission={detailSubmission} />

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteId} onClose={() => !deleting && setDeleteId(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Delete Submission</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to permanently delete this submission? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteId(null)}
            disabled={deleting}
            sx={{ textTransform: "none", borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ textTransform: "none", borderRadius: "8px", bgcolor: "#EF4444", "&:hover": { bgcolor: "#DC2626" }, minWidth: 90 }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSurveySubmissions;


